import React, { useEffect, useRef, useState } from "react";
import styles from "./ChatbotPanel.module.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import SendIcon from "@mui/icons-material/Send";

import CourseCard from "./CourseCard";
import JobCard from "./JobCard";
import ProfileCard from "./ProfileCard";

const normalizeUrlToCurrentOrigin = (url) => {
  try {
    const targetUrl = new URL(url);
    const { protocol, host } = window.location;

    if (!targetUrl.hostname.includes("nxnearby.com")) {
      return url;
    }

    targetUrl.protocol = protocol;
    targetUrl.host = host;

    return targetUrl.toString();
  } catch {
    return url;
  }
};

/* ---------- LINKIFY HELPER ---------- */
const linkify = (text) => {
  if (!text) return text;

  const urlRegex = /(https?:\/\/[^\s]+)/g;

  return text.split(urlRegex).map((part, i) =>
    part.match(urlRegex) ? (
      <a
        key={i}
        href={normalizeUrlToCurrentOrigin(part)}
        target="_blank"
        rel="noreferrer"
        className={styles.analysisLink}
        onClick={(e) => e.stopPropagation()}
      >
        View full course →
      </a>
    ) : (
      part
    )
  );
};

/* ---------- ANALYSIS RENDERER ---------- */
const renderAnalysis = (text) => {
  if (!text) return null;

  const blocks = text.split("\n\n");

  return blocks.map((block, idx) => {
    const lines = block.split("\n");

    if (lines.some((l) => l.trim().startsWith("- "))) {
      const heading =
        lines[0].endsWith(":") && !lines[0].trim().startsWith("-")
          ? lines[0]
          : null;

      const bullets = lines.filter((l) => l.trim().startsWith("- "));

      return (
        <div key={idx}>
          {heading && (
            <div className={styles.sectionTitle}>
              {heading.replace(":", "")}
            </div>
          )}
          <ul>
            {bullets.map((b, i) => (
              <li key={i}>{linkify(b.replace("- ", ""))}</li>
            ))}
          </ul>
        </div>
      );
    }

    if (block.endsWith(":")) {
      return (
        <div key={idx} className={styles.sectionTitle}>
          {block.replace(":", "")}
        </div>
      );
    }

    return (
      <p key={idx} className={styles.paragraph}>
        {linkify(block)}
      </p>
    );
  });
};

const ExpandableList = ({
  items = [],
  renderItem,
  initialCount = 3,
  label,
}) => {
  const [expanded, setExpanded] = useState(false);

  if (!items.length) return null;

  const visibleItems = expanded
    ? items
    : items.slice(0, initialCount);

  const hasToggle = items.length > initialCount;

  return (
    <>
      {visibleItems.map((item, i) => renderItem(item, i))}

      {hasToggle && (
        <button
          className={styles.showMoreBtn}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded
            ? "Show less"
            : `Show ${items.length - initialCount} more ${label}`}
        </button>
      )}
    </>
  );
};

const ChatView = ({ mode = "courses", onBack, onClose, embedded = false }) => {
  const [messages, setMessages] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const bottomRef = useRef(null);

  const userData = JSON.parse(localStorage.getItem("userData"));
  const token = userData?.token;

  const endpoints = {
    courses: `${process.env.REACT_APP_API_BASE_URL}api/v1/suggestions/courses/ai_search/`,
    jobs: `${process.env.REACT_APP_API_BASE_URL}api/v1/jobs/ai_search/`,
    profile: `${process.env.REACT_APP_API_BASE_URL}api/v1/profile/filters/ai_search/`,
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const sendMessage = async () => {
    if (!query.trim() || loading) return;

    const userText = query.trim();
    setQuery("");

    setMessages((prev) => [...prev, { role: "user", text: userText }]);
    setLoading(true);

    try {
      const res = await fetch(endpoints[mode], {
        method: "POST",
        headers: {
          Authorization: `Token ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ query: userText }),
      });

      const data = await res.json();
      setMessages((prev) => [...prev, { role: "ai", data }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "ai",
          data: { ai_analysis: "Something went wrong. Please try again." },
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`${styles.chatWrapper} ${styles[mode]}`}>
      {!embedded && ( 
        <div className={styles.globalHeader}>
          <div>
            <h4>nxNearby's AI Assistant</h4>
            <p>Your personal AI helper</p>
          </div>
          <button className={styles.iconBtn} onClick={onClose}>
            <CloseRoundedIcon />
          </button>
        </div>
      )}

      <div className={styles.contextHeader}>
        <button className={styles.iconBtn} onClick={onBack}>
          <ArrowBackIcon />
        </button>

        <div className={styles.contextTitle}>
          <h4>
            {mode === "courses"
              ? "Course Assistant"
              : mode === "jobs"
              ? "Job Assistant"
              : "Profile Assistant"}
          </h4>
          <p>AI-powered recommendations</p>
        </div>

        <div className={styles.sparkle}>
          <AutoAwesomeOutlinedIcon fontSize="small" />
        </div>
      </div>

      <div className={`${styles.chatBody} ${embedded ? styles.embeddedChatBody : null}`}>
        {messages.length === 0 && (
          <div className={`${styles.message} ${styles.aiMsg}`}>
            <p>
              Hi! I'm here to help you find the best{" "}
              {mode === "courses" ? "courses" : mode}. What are you looking for?
            </p>
            <span className={styles.time}>Just now</span>
          </div>
        )}

        {messages.map((msg, index) => (
          <React.Fragment key={index}>
            {msg.role === "user" && (
              <div className={`${styles.message} ${styles.userMsg}`}>
                <p>{msg.text}</p>
                <span className={styles.time}>Just now</span>
              </div>
            )}

            {msg.role === "ai" && (
              <div className={`${styles.message} ${styles.aiMsg}`}>
                {msg.data?.ai_analysis && (
                  <div className={styles.analysis}>
                    {renderAnalysis(msg.data.ai_analysis)}
                  </div>
                )}

                {msg.data?.suggestions?.length > 0 && (
                  <div className={styles.analysis}>
                    <div className={styles.sectionTitle}>Suggestions</div>
                    <ul>
                      {msg.data.suggestions.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <span className={styles.time}>Just now</span>

                {mode === "courses" && (
                  <ExpandableList
                    items={msg.data?.data?.all_courses || []}
                    label="courses"
                    renderItem={(course, i) => (
                      <CourseCard key={i} course={course} />
                    )}
                  />
                )}

                {mode === "jobs" && (
                  <ExpandableList
                    items={msg.data?.data?.jobs || []}
                    label="jobs"
                    renderItem={(job, i) => (
                      <JobCard key={i} job={job} />
                    )}
                  />
                )}

                {mode === "profile" && (
                  <ExpandableList
                    items={msg.data?.data?.profiles || []}
                    label="profiles"
                    renderItem={(profile, i) => (
                      <ProfileCard key={i} profile={profile} />
                    )}
                  />
                )}
              </div>
            )}
          </React.Fragment>
        ))}

        {loading && (
          <div className={`${styles.message} ${styles.aiMsg}`}>
            <p>Thinking…</p>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <div className={styles.inputBar}>
        <textarea
          placeholder={`Ask about ${mode}`}
          value={query}
          rows={1}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
        />
        <button onClick={sendMessage} disabled={loading || !query.trim()}>
          <SendIcon />
        </button>
      </div>
    </div>
  );
};

export default ChatView;